import * as React from 'react'
import type { AppointmentsMetricDefinition, LeadsMetricDefinition, VenueLeadStatsSettingsDto } from '@lib/dto/lead-stats.dto'
import type { PutVenueLeadStatsAdjustmentsInput } from '@lib/input/lead-stats.input'
import { Button } from '@/components/ui/button'
import { FormActions } from '@/components/ui/form-actions'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSaveVenueLeadStatsSettings, useVenueLeadStatsSettings } from '@/hooks/useLeadStats'
import { AppointmentsDefinitionFields, LeadsDefinitionFields, NameField } from './DefinitionFields'

type MetricRow = VenueLeadStatsSettingsDto['metrics'][number]

/** Names in a short sentence, or what an empty list means. */
function listed(names: readonly string[], none: string): string {
  return names.length === 0 ? none : names.join(', ')
}

/** A leads number's definition, in a line. */
function describeLeads(d: LeadsMetricDefinition): string {
  const parts = [
    d.stages.length > 0 ? `stages ${d.stages.join(', ')}` : null,
    d.results.length > 0 ? `results ${d.results.join(', ')}` : null,
    d.tagsAny.length > 0 ? `tagged ${d.tagsAny.join(' or ')}` : null,
    d.tagsNone.length > 0 ? `not tagged ${d.tagsNone.join(' or ')}` : null,
    d.sources.length > 0 ? `from ${d.sources.join(', ')}` : null,
  ].filter((p): p is string => p !== null)
  return parts.length === 0 ? 'Every lead in the pipeline' : `Couples ${parts.join('; ')}`
}

/** An appointments number's definition, in a line. */
function describeAppointments(d: AppointmentsMetricDefinition): string {
  return `Appointments on ${d.calendars.join(', ')}, counted on the day ${d.countBy === 'held' ? 'they are for' : 'they were booked'}`
}

/** The company's definition of a number, in a line. */
function summary(row: MetricRow): string {
  if (row.metric.kind === 'leads') return describeLeads(row.metric.definition)
  if (row.metric.kind === 'appointments') return describeAppointments(row.metric.definition)
  return ''
}

/** What a number is made of at this venue: its own adjustment, or the company's. */
function venueSummary(row: MetricRow): string {
  if (row.kind === 'leads' && row.definition !== null) return `${describeLeads(row.definition)}, at this venue.`
  if (row.kind === 'appointments' && row.definition !== null) return `${describeAppointments(row.definition)}, at this venue.`
  return `${summary(row)}, as the company has it.`
}

/**
 * A venue's own ingredients for the company's numbers (docs/gohighlevel-stats.spec.md,
 * "Numbers"): which GoHighLevel pipelines make up each named pipeline here, and
 * what each number is made of. Its staff can't add, drop or rename numbers;
 * those are the company's, so every venue can be compared.
 */
export function VenueLeadStatsSection({
  venueId,
  canEdit,
}: {
  venueId: string
  /** Edit adds adjusting what the venue's numbers are made of; without it they are only read. */
  canEdit: boolean
}) {
  const query = useVenueLeadStatsSettings(venueId, true)
  if (query.isPending) return <DetailSkeleton />
  if (query.isError) return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  if (!canEdit) return <VenueLeadStatsReadOnly settings={query.data} />
  return <VenueLeadStatsEditor key={query.dataUpdatedAt} venueId={venueId} settings={query.data} />
}

/** No numbers yet: the company defines them, not the venue. */
function NoNumbersYet() {
  return (
    <p className="m-0 text-sm text-ink-muted">
      The company hasn’t defined any numbers yet. An organization admin does that on the company’s GoHighLevel page.
    </p>
  )
}

/** What each pipeline and number is made of at this venue, for someone who can't change it. */
function VenueLeadStatsReadOnly({ settings }: { settings: VenueLeadStatsSettingsDto }) {
  if (settings.pipelines.length === 0 && settings.metrics.length === 0) return <NoNumbersYet />
  return (
    <div className="space-y-6">
      {settings.pipelines.map((row) => (
        <Card key={row.pipeline.id} className="space-y-2 p-3">
          <h3 className="m-0 text-sm font-semibold text-ink">{row.pipeline.name}</h3>
          <p className="m-0 text-sm text-ink-muted">
            {row.ghlPipelineNames === null
              ? `Made of ${listed(row.pipeline.ghlPipelineNames, 'no pipelines')}, as the company has it.`
              : `Made of ${listed(row.ghlPipelineNames, 'no pipelines')} at this venue.`}
          </p>
        </Card>
      ))}

      {settings.metrics.map((row) => (
        <Card key={row.metric.id} className="space-y-2 p-3">
          <h3 className="m-0 text-sm font-semibold text-ink">{row.metric.name}</h3>
          <p className="m-0 text-sm text-ink-muted">{venueSummary(row)}</p>
        </Card>
      ))}
    </div>
  )
}

function VenueLeadStatsEditor({ venueId, settings }: { venueId: string; settings: VenueLeadStatsSettingsDto }) {
  const { toast } = useToast()
  const save = useSaveVenueLeadStatsSettings(venueId)
  const [pipelines, setPipelines] = React.useState(settings.pipelines)
  const [metrics, setMetrics] = React.useState(settings.metrics)
  const [error, setError] = React.useState<string | null>(null)
  const choices = settings.choices

  if (settings.pipelines.length === 0 && settings.metrics.length === 0) return <NoNumbersYet />

  function setLeads(index: number, definition: LeadsMetricDefinition | null): void {
    setMetrics((all) => all.map((m, i) => (i === index && m.kind === 'leads' ? { ...m, definition } : m)))
  }

  function setAppointments(index: number, definition: AppointmentsMetricDefinition | null): void {
    setMetrics((all) => all.map((m, i) => (i === index && m.kind === 'appointments' ? { ...m, definition } : m)))
  }

  function handleSave(): void {
    const input: PutVenueLeadStatsAdjustmentsInput = {
      pipelines: pipelines.map((p) => ({ pipelineId: p.pipeline.id, ghlPipelineNames: p.ghlPipelineNames })),
      metrics: metrics.map((m) => ({ metricId: m.metric.id, definition: m.definition })),
    }
    if (pipelines.some((p) => p.ghlPipelineNames !== null && p.ghlPipelineNames.length === 0)) {
      setError('Choose at least one GoHighLevel pipeline, or use the company’s.')
      return
    }
    setError(null)
    save.mutate(input, {
      onSuccess: () => toast('Saved. This venue’s numbers now count this way.'),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  return (
    <div className="space-y-6">
      {pipelines.map((row, index) => (
        <Card key={row.pipeline.id} className="space-y-2 p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="m-0 text-sm font-semibold text-ink">{row.pipeline.name}</h3>
            <Button
              variant="link"
              size="sm"
              onClick={() =>
                setPipelines((all) =>
                  all.map((p, i) =>
                    i === index ? { ...p, ghlPipelineNames: p.ghlPipelineNames === null ? [...p.pipeline.ghlPipelineNames] : null } : p,
                  ),
                )
              }
            >
              {row.ghlPipelineNames === null ? 'Adjust for this venue' : 'Use the company’s'}
            </Button>
          </div>
          {row.ghlPipelineNames === null ? (
            <p className="m-0 text-sm text-ink-muted">
              Made of {listed(row.pipeline.ghlPipelineNames, 'no pipelines')}, as the company has it.
            </p>
          ) : (
            <NameField
              id={`venue-pipeline-${row.pipeline.id}`}
              label="GoHighLevel pipelines at this venue"
              available={choices.pipelines.map((p) => p.name)}
              chosen={row.ghlPipelineNames}
              onChange={(names) => setPipelines((all) => all.map((p, i) => (i === index ? { ...p, ghlPipelineNames: names } : p)))}
            />
          )}
        </Card>
      ))}

      {metrics.map((row, index) => (
        <Card key={row.metric.id} className="space-y-2 p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="m-0 text-sm font-semibold text-ink">{row.metric.name}</h3>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                if (row.kind === 'leads' && row.metric.kind === 'leads')
                  setLeads(index, row.definition === null ? row.metric.definition : null)
                if (row.kind === 'appointments' && row.metric.kind === 'appointments') {
                  setAppointments(index, row.definition === null ? row.metric.definition : null)
                }
              }}
            >
              {row.definition === null ? 'Adjust for this venue' : 'Use the company’s'}
            </Button>
          </div>
          {row.definition === null ? <p className="m-0 text-sm text-ink-muted">{summary(row)}, as the company has it.</p> : null}
          {row.kind === 'leads' && row.definition !== null ? (
            <LeadsDefinitionFields
              idPrefix={`venue-metric-${row.metric.id}`}
              definition={row.definition}
              choices={choices}
              onChange={(definition) => setLeads(index, definition)}
            />
          ) : null}
          {row.kind === 'appointments' && row.definition !== null ? (
            <AppointmentsDefinitionFields
              idPrefix={`venue-metric-${row.metric.id}`}
              definition={row.definition}
              choices={choices}
              onChange={(definition) => setAppointments(index, definition)}
            />
          ) : null}
        </Card>
      ))}

      {error === null ? null : <FormError>{error}</FormError>}
      <FormActions>
        <Button
          variant="outline"
          onClick={() => {
            setPipelines(settings.pipelines)
            setMetrics(settings.metrics)
            setError(null)
          }}
          disabled={save.isPending}
        >
          Discard changes
        </Button>
        <Button onClick={handleSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </FormActions>
    </div>
  )
}
